import './App.css';
import type { WidgetConfig } from '@rango-dev/widget-embedded';

import { useSocialWallet } from '@rango-dev/internal-social-wallet';
import { WidgetProvider } from '@rango-dev/widget-embedded';
import React, { useEffect, useMemo } from 'react';

import App from './App';
import { useAppDispatch, useAppSelector } from './hooks/reduxHooks';
import { useIsDesktopNavigation } from './hooks/useIsDesktopNavigation';
import { useSubscribeToEvents } from './hooks/useSubscribeToEvents';
import { useSyncStoreWithLocalStorage } from './hooks/useSyncStoreWithLocalStorage';
import useThemeAutoSwitch from './hooks/useThemeAutoSwitch';
import { setExcludedBlockchains, setExcludedWallets } from './state/WalletDetailsSlice';
import { getActiveThemeClassName } from './styles/themeUtility';
import { WalletEvents } from './utils/Constants';
import { getProviders } from './utils/wallets/general';

function Bootstrap(): JSX.Element {
  const dispatch = useAppDispatch();
  const widgetConfig = useAppSelector((state) => state.widget);
  const { isDarkMode, theme } = useAppSelector((state) => state.settings);
  const isDesktopNavigation = useIsDesktopNavigation();
  useThemeAutoSwitch(theme);
  useSubscribeToEvents();
  useSyncStoreWithLocalStorage();
  const { provider: privyProvider } = useSocialWallet();

  const providers = useMemo(() => getProviders(), []);

  useEffect(() => {
    const classes = getActiveThemeClassName(isDarkMode);

    classes.forEach((cls) => document.body.classList.add(cls));

    return () => {
      classes.forEach((cls) => document.body.classList.remove(cls));
    };
  }, [isDarkMode]);

  return (
    <div className="App">
      <WidgetProvider
        config={{
          ...widgetConfig,
          theme: {
            ...widgetConfig.theme,
            mode: isDarkMode ? 'dark' : 'light',
          },
          features: {
            ...widgetConfig.features,
            notification: isDesktopNavigation ? 'hidden' : 'visible',
          },
          /*
           * `privyProvider` is typed `Provider<{ evm; solana }>` (SocialWalletProvider),
           * a more specific instantiation than the bare `Provider<unknown>` the widget
           * config expects. `@hub3js/core`'s `Provider` is invariant over its namespace
           * generic (it surfaces `TNS` through `info()`), so the narrower type isn't
           * assignable to the wider one even though it's safe at runtime. Cast to the
           * config's wallet type.
           */
          wallets: (!!privyProvider
            ? [privyProvider, ...providers]
            : providers) as WidgetConfig['wallets'],
        }}
        onUpdateState={(_type, name) => {
          /*
           * If any updates has been triggered,
           * We push the update to redux for compatibility with the current architecture.
           */
          if (name === WalletEvents.ACCOUNTS) {
            // reset wallet details filters
            dispatch(setExcludedBlockchains([]));
            dispatch(setExcludedWallets([]));
          }
        }}>
        <App />
      </WidgetProvider>
    </div>
  );
}

export default Bootstrap;
